import { useState, type SyntheticEvent } from "react";
import type { Photo } from "../../../shared/photos";
import { useAppUi } from "../../app/uiContext";
import { Icon } from "../../ui/Icon";
import { Confirm } from "../../ui/Sheet";
import { useMarkPhoto, usePhotoMarks } from "./api";

const stop = (e: SyntheticEvent) => e.stopPropagation();

/**
 * Like and flag buttons on a photo. A like is a heart every screen shows. A flag takes
 * the photo off every screen for good, like a photo kept private (api.ts, docs/MEDIA.md).
 */
export function PhotoMarkButtons({ photo }: { photo: Photo }) {
  const marks = usePhotoMarks().data;
  const mark = useMarkPhoto();
  const { toast } = useAppUi();
  const [asking, setAsking] = useState(false);
  const liked = marks?.liked.includes(photo.id) ?? false;

  const like = () =>
    mark.mutate({ id: photo.id, change: { liked: !liked } }, { onError: (e) => toast(`Couldn’t save that: ${(e as Error).message}`) });
  const flag = () => {
    setAsking(false);
    mark.mutate(
      { id: photo.id, change: { flagged: true } },
      {
        onSuccess: () => toast("Photo taken off the wall. It won’t be shown again."),
        onError: (e) => toast(`Hidden on this screen, but it didn’t save: ${(e as Error).message}`),
      },
    );
  };

  return (
    <>
      <button className={`pf-round pf-like ${liked ? "is-on" : ""}`} onClick={like} aria-label={liked ? "Unlike this photo" : "Like this photo"} aria-pressed={liked}>
        <Icon name="heart" size="1.15rem" />
      </button>
      <button className="pf-round pf-flag" onClick={() => setAsking(true)} aria-label="Flag this photo so it’s never shown">
        <Icon name="flag" size="1.1rem" />
      </button>
      {asking && (
        // The sheet is a portal, but its taps still bubble through React to the slideshow (pause, swipe).
        <span className="pf-confirm" onPointerDown={stop} onPointerUp={stop} onClick={stop} onKeyDown={stop}>
          <Confirm
            title="Never show this photo?"
            message="It comes off every screen and the wall for good, like the photos kept private."
            confirmLabel="Never show it"
            danger
            onCancel={() => setAsking(false)}
            onConfirm={flag}
          />
        </span>
      )}
    </>
  );
}
